import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { useState } from 'react'

import { GoodErrorMessaging } from 'types/internal'

export function useStripeSubmission({
  action,
  postalCode,
  onTokenCreated,
  onCleared,
}: {
  action: string
  postalCode?: string
  onTokenCreated(token: string): Promise<unknown>
  onCleared?(): void
}) {
  const elements = useElements()
  const stripe = useStripe()

  const [isProcessingSubmit, setIsProcessingSubmit] = useState(false)
  const [submissionError, setSubmissionError] =
    useState<GoodErrorMessaging | null>(null)

  const onSubmit = async () => {
    setSubmissionError(null)

    const goodErrorMessaging = {
      helpToFix: 'Please reload the page and try again.',
      why: `We couldn't ${action}.`,
    }

    const cardElement = elements?.getElement(CardNumberElement)

    if (!stripe || !cardElement) {
      setSubmissionError(goodErrorMessaging)
      return
    }

    setIsProcessingSubmit(true)

    const { error, token } = await stripe.createToken(cardElement, {
      address_zip: postalCode || undefined,
    })

    if (error || !token) {
      if (error?.type === 'validation_error') {
        setSubmissionError({
          helpToFix:
            error.message ?? 'Please check your card details and try again.',
          why: `We couldn't ${action}.`,
        })
      } else {
        setSubmissionError(goodErrorMessaging)
      }
    } else {
      try {
        await onTokenCreated(token.id)

        elements?.getElement(CardNumberElement)?.clear()
        elements?.getElement(CardExpiryElement)?.clear()
        elements?.getElement(CardCvcElement)?.clear()
        onCleared?.()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        // Caller is expected to handle any error here.
      }
    }

    setIsProcessingSubmit(false)
  }

  return {
    isProcessingSubmit,
    onSubmit,
    submissionError,
  }
}
